import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Plus, Activity, Copy, Trash2, Eye, Pencil, Printer, Filter, X } from "lucide-react";
import { toast } from "sonner";
import { printNursingRecord, printNursingRecordsBatch } from "../lib/printDoc";
import { useAuth } from "../context/AuthContext";

const DAILY_LIVING_ITEMS = [
  "食事", "水分摂取", "排泄(尿)", "排泄(便)", "入浴・清潔",
  "更衣", "移動", "移乗", "睡眠", "服薬管理",
  "整容", "家事", "コミュニケーション", "外出",
];

const OBSERVATION_CHECK_ITEMS = [
  "意識状態", "言動・行動", "睡眠", "食事摂取", "水分摂取",
  "排泄(尿)", "排泄(便)", "服薬状況", "皮膚状態",
  "疼痛", "創傷・褥瘡", "家族との関係",
];

const OBSERVATION_OPTIONS = {
  "意識状態": [
    "JCS 0(清明)",
    "JCS Ⅰ-1(意識清明だがいまひとつはっきりしない)",
    "JCS Ⅰ-2(見当識障害あり)",
    "JCS Ⅰ-3(自分の名前・生年月日が言えない)",
    "JCS Ⅱ-10(普通の呼びかけで開眼)",
    "JCS Ⅱ-20(大声・身体刺激で開眼)",
    "JCS Ⅱ-30(痛み刺激で辛うじて開眼)",
    "JCS Ⅲ-100(痛み刺激に払いのける動作)",
    "JCS Ⅲ-200(痛み刺激で手足を動かす・顔をしかめる)",
    "JCS Ⅲ-300(痛み刺激に全く反応しない)",
  ],
  "表情": ["穏やか", "明るい", "苦痛様", "無表情", "落胆", "不安様"],
  "言動・行動": ["落ち着いている", "活発", "緩慢", "興奮", "暴言", "拒否的", "異常なし"],
  "睡眠": ["良眠", "中途覚醒", "入眠困難", "不眠", "過眠", "昼夜逆転"],
  "食事摂取": ["全量摂取", "8割摂取", "半量摂取", "少量のみ", "拒否", "経管栄養", "禁食"],
  "水分摂取": ["良好", "やや少なめ", "不足", "制限あり", "経管"],
  "排泄(尿)": ["自立", "見守り", "一部介助", "全介助", "オムツ", "バルーン留置", "失禁"],
  "排泄(便)": ["自立", "見守り", "一部介助", "全介助", "オムツ", "便秘", "下痢", "ストーマ"],
  "服薬状況": ["自己管理", "見守りで自己管理", "介助下で完了", "一部介助", "全介助", "拒否", "飲み忘れあり"],
  "呼吸状態": ["安定", "やや浅い", "努力呼吸", "喘鳴あり", "痰絡み", "SpO2低下", "酸素使用中"],
  "循環(脈・血圧)": ["安定", "やや高値", "やや低値", "高値", "低値", "不整脈あり"],
  "皮膚状態": ["良好", "乾燥", "発赤", "湿疹", "浮腫", "創傷あり", "黄疸"],
  "浮腫": ["なし", "軽度", "中等度", "高度", "下肢のみ", "全身"],
  "疼痛": ["なし", "軽度", "中等度", "強い", "鎮痛剤で軽減", "コントロール不良"],
  "発熱": ["なし(平熱)", "微熱(37℃台)", "中等度発熱(38℃台)", "高熱(39℃以上)", "解熱傾向"],
  "創傷・褥瘡": ["なし", "経過良好", "停滞", "悪化", "新規発生", "感染兆候あり"],
  "ADL": ["自立", "見守り", "一部介助", "全介助", "寝たきり"],
  "家族との関係": ["良好", "普通", "介護負担あり", "緊張あり", "不在がち", "キーパーソン安定"],
};
const BATH_GROOMING_ITEMS = [
  "全身浴", "シャワー浴", "部分浴(足浴)", "部分浴(手浴)", "清拭", "陰部洗浄",
  "洗髪", "整髪", "口腔ケア", "髭剃り", "爪切り(手)", "爪切り(足)",
  "着替え", "更衣介助",
];

const BATH_GROOMING_OPTIONS = ["実施なし", "自立", "見守り", "一部介助", "全介助", "拒否", "次回実施"];

function CheckGrid({ items, value, onChange, testid }) {
  const set = new Set(value || []);
  const toggle = (item) => {
    const next = new Set(set);
    if (next.has(item)) next.delete(item); else next.add(item);
    onChange(Array.from(next));
  };
  return (
    <div data-testid={testid} className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
      {items.map((item) => {
        const checked = set.has(item);
        return (
          <label
            key={item}
            data-testid={`${testid}-${item}`}
            className={`flex items-center gap-2 px-2.5 py-1.5 rounded border cursor-pointer text-xs transition-colors ${
              checked
                ? "bg-[#2C5545] border-[#2C5545] text-white"
                : "bg-white border-[#E5E5E3] text-[#1C1C1A] hover:border-[#2C5545]"
            }`}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => toggle(item)}
              className="w-3.5 h-3.5"
            />
            <span>{item}</span>
          </label>
        );
      })}
    </div>
  );
}

export default function Records() {
  const { user } = useAuth();
  const isAdmin = user?.role === "tenant_admin" || user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [org, setOrg] = useState({});
  const [patientFilter, setPatientFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [nurseFilter, setNurseFilter] = useState("");
  const [professionFilter, setProfessionFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [viewRecord, setViewRecord] = useState(null);
  const [form, setForm] = useState(initForm());
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open new dialog when ?new=1 (from dashboard quick action)
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  function initForm() {
    return {
      patient_id: "",
      record_date: new Date().toISOString().slice(0, 10),
      nurse_name: "", visit_profession: "看護師", visit_location: "",
      daily_living: "", mental_state: "", nursing_actions: "",
      gaf_score: "", next_visit_date: "",
      temperature: "", bp_systolic: "", bp_diastolic: "", pulse: "", spo2: "", respiration: "",
      soap_s: "", soap_o: "", soap_a: "", soap_p: "",
      procedures: "", observations: "", summary: "",
      observation_checks: [], observation_details: {}, bath_grooming_checks: [], bath_grooming_details: {},
      daily_living_details: {},
    };
  }

  const load = () => {
    api.get("/records", { params: { patient_id: patientFilter || undefined } }).then((r) => setItems(r.data));
  };

  useEffect(() => {
    api.get("/patients").then((r) => setPatients(r.data));
    api.get("/users").then((r) => setStaff(r.data || [])).catch(() => setStaff([]));
    api.get("/organization").then((r) => setOrg(r.data || {})).catch(() => setOrg({}));
  }, []);
  useEffect(load, [patientFilter]);

  const save = async () => {
    if (!form.patient_id) { toast.error("利用者を選択してください"); return; }
    const body = { ...form };
    ["temperature", "bp_systolic", "bp_diastolic", "pulse", "spo2", "respiration", "gaf_score"].forEach((k) => {
      body[k] = body[k] === "" ? null : Number(body[k]);
    });
    ["next_visit_date"].forEach((k) => {
      if (body[k] === "") body[k] = null;
    });
    if (editId) {
      await api.patch(`/records/${editId}`, body);
      toast.success("記録を更新しました");
    } else {
      await api.post("/records", body);
      toast.success("記録を保存しました");
    }
    setOpen(false);
    setEditId(null);
    setForm(initForm());
    load();
  };

  const startEdit = (r) => {
    setEditId(r.id);
    setForm({
      patient_id: r.patient_id || "",
      record_date: r.record_date || new Date().toISOString().slice(0, 10),
      nurse_name: r.nurse_name || "",
      visit_profession: r.visit_profession || "看護師",
      visit_location: r.visit_location || "",
      daily_living: r.daily_living || "",
      mental_state: r.mental_state || "",
      nursing_actions: r.nursing_actions || "",
      gaf_score: r.gaf_score ?? "",
      next_visit_date: r.next_visit_date || "",
      temperature: r.temperature ?? "",
      bp_systolic: r.bp_systolic ?? "",
      bp_diastolic: r.bp_diastolic ?? "",
      pulse: r.pulse ?? "",
      spo2: r.spo2 ?? "",
      respiration: r.respiration ?? "",
      soap_s: r.soap_s || "",
      soap_o: r.soap_o || "",
      soap_a: r.soap_a || "",
      soap_p: r.soap_p || "",
      procedures: r.procedures || "",
      observations: r.observations || "",
      observation_checks: Array.isArray(r.observation_checks) ? r.observation_checks : [],
      observation_details: (r.observation_details && typeof r.observation_details === "object") ? r.observation_details : {},
      bath_grooming_checks: Array.isArray(r.bath_grooming_checks) ? r.bath_grooming_checks : [],
      daily_living_details: (r.daily_living_details && typeof r.daily_living_details === "object") ? r.daily_living_details : {},
      summary: r.summary || "",
    });
    setOpen(true);
  };

  const del = async (id) => {
    if (!confirm("削除しますか?")) return;
    await api.delete(`/records/${id}`);
    load();
  };

  const patientName = (pid) => patients.find((p) => p.id === pid)?.name_kanji || "?";
  const patientById = (pid) => patients.find((p) => p.id === pid);

  const printRecord = (r) => printNursingRecord(r, patientById(r.patient_id), org);

  // クライアントサイドのフィルタ(API側 patient_id 絞り込みに加えて)
  const filteredItems = items.filter((r) => {
    if (dateFrom && r.record_date && r.record_date < dateFrom) return false;
    if (dateTo && r.record_date && r.record_date > dateTo) return false;
    if (nurseFilter && (r.nurse_name || "") !== nurseFilter) return false;
    if (professionFilter && (r.visit_profession || "") !== professionFilter) return false;
    return true;
  });

  const filtersActive = !!(patientFilter || dateFrom || dateTo || nurseFilter || professionFilter);
  const clearFilters = () => {
    setPatientFilter("");
    setDateFrom("");
    setDateTo("");
    setNurseFilter("");
    setProfessionFilter("");
  };

  const printBatch = () => {
    if (filteredItems.length === 0) {
      toast.error("印刷対象の記録がありません");
      return;
    }
    if (filteredItems.length > 100 &&
        !window.confirm(`${filteredItems.length}件を一括印刷します。よろしいですか?`)) {
      return;
    }
    const enriched = filteredItems.map((r) => ({
      record: r,
      patient: patientById(r.patient_id) || { name_kanji: patientName(r.patient_id) },
    }));
    const labelParts = [];
    if (patientFilter) labelParts.push(`利用者: ${patientName(patientFilter)}`);
    if (dateFrom || dateTo) labelParts.push(`期間: ${dateFrom || "─"} 〜 ${dateTo || "─"}`);
    if (nurseFilter) labelParts.push(`看護師: ${nurseFilter}`);
    if (professionFilter) labelParts.push(`職種: ${professionFilter}`);
    printNursingRecordsBatch(enriched, org, labelParts.join(" / "));
    toast.success(`${filteredItems.length}件を印刷ウィンドウで開きました`);
  };

  return (
    <div className="space-y-6" data-testid="records-page">
      <div className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#8A8A86] font-numeric mb-2">NURSING RECORDS</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">看護記録</h1>
          <p className="text-[#4A4A48] mt-1">訪問看護記録書Ⅱ · SOAP形式</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditId(null); setForm(initForm()); } }}>
          <DialogTrigger asChild>
            <Button data-testid="new-record-button" className="bg-[#2C5545] text-white"><Plus size={16} className="mr-2" />新規記録</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editId ? "看護記録 編集" : "看護記録 新規作成"}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-3">
              <div className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-[#F8FAF8] border border-[#2C5545]/20">
                <div className="text-xs text-[#4A4A48]">
                  {form.patient_id
                    ? "選択中の利用者の前回記録から、記録日と署名を除く全項目を上書きします"
                    : "利用者を選択すると、その利用者の前回記録を取り込めます"}
                </div>
                <button
                  type="button"
                  data-testid="load-prev-record-all"
                  disabled={!form.patient_id}
                  onClick={async () => {
                    if (!form.patient_id) {
                      toast.error("先に利用者を選択してください");
                      return;
                    }
                    try {
                      const r = await api.get(`/records?patient_id=${form.patient_id}`);
                      const list = (r.data || [])
                        .filter((x) => x.id !== editId)
                        .sort((a, b) => (b.record_date || "").localeCompare(a.record_date || ""));
                      if (list.length === 0) {
                        toast.error("この利用者の過去記録が見つかりません");
                        return;
                      }
                      const prev = list[0];
                      // 記録日 / 自動付与系 / 利用者IDは引き継がない
                      const skip = new Set(["id", "patient_id", "record_date", "tenant_id", "staff_id", "visit_id", "created_at", "updated_at", "next_visit_date"]);
                      const next = { ...form };
                      Object.entries(prev).forEach(([k, v]) => {
                        if (skip.has(k)) return;
                        if (v === null || v === undefined) return;
                        next[k] = v;
                      });
                      // observation_details / bath_grooming_details / daily_living_details は object として保持
                      ["observation_details", "bath_grooming_details", "daily_living_details"].forEach((k) => {
                        next[k] = (prev[k] && typeof prev[k] === "object") ? { ...prev[k] } : (form[k] || {});
                      });
                      // bath_grooming_checks の旧データ互換
                      if (Array.isArray(prev.bath_grooming_checks) && Object.keys(next.bath_grooming_details || {}).length === 0) {
                        next.bath_grooming_details = prev.bath_grooming_checks.reduce((a, c) => { a[c] = "実施"; return a; }, {});
                      }
                      setForm(next);
                      toast.success(`${prev.record_date} の記録から全項目を取り込みました`);
                    } catch (e) {
                      toast.error("読み込みに失敗しました");
                    }
                  }}
                  className="shrink-0 text-xs flex items-center gap-1 px-3 py-1.5 rounded border border-[#2C5545] text-[#2C5545] hover:bg-[#2C5545] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#2C5545]"
                >
                  <Copy size={13} /> 前回記録を全て読み込み
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>利用者</Label>
                  <select value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })} className="flex h-10 w-full rounded-md border border-input px-3 text-sm">
                    <option value="">選択</option>
                    {patients.map((p) => <option key={p.id} value={p.id}>{p.name_kanji}</option>)}
                  </select>
                </div>
                <div><Label>記録日</Label><Input type="date" value={form.record_date} onChange={(e) => setForm({ ...form, record_date: e.target.value })} /></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">看護師等氏名</Label>
                  <select
                    data-testid="nurse-name-input"
                    value={form.nurse_name}
                    onChange={(e) => setForm({ ...form, nurse_name: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input px-3 text-sm bg-white"
                  >
                    <option value="">選択してください</option>
                    {staff.map((s) => (
                      <option key={s.id} value={s.name}>
                        {s.name}{s.role ? ` (${roleLabel(s.role)})` : ""}
                      </option>
                    ))}
                    {form.nurse_name && !staff.some((s) => s.name === form.nurse_name) && (
                      <option value={form.nurse_name}>{form.nurse_name} (登録外)</option>
                    )}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">訪問職種</Label>
                  <select
                    data-testid="visit-profession-select"
                    value={form.visit_profession}
                    onChange={(e) => setForm({ ...form, visit_profession: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input px-3 text-sm bg-white"
                  >
                    {["看護師", "准看護師", "保健師", "理学療法士", "作業療法士", "言語聴覚士", "精神保健福祉士", "その他"].map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">訪問先</Label>
                  <select
                    data-testid="visit-location-input"
                    value={form.visit_location}
                    onChange={(e) => setForm({ ...form, visit_location: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input px-3 text-sm bg-white"
                  >
                    <option value="">選択してください</option>
                    {["自宅", "サービス付き高齢者向け住宅", "グループホーム", "ケアハウス", "有料老人ホーム", "特別養護老人ホーム", "介護老人保健施設", "障害者支援施設", "病院", "クリニック", "デイサービス", "短期入所(ショートステイ)", "その他"].map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                    {form.visit_location && !["自宅", "サービス付き高齢者向け住宅", "グループホーム", "ケアハウス", "有料老人ホーム", "特別養護老人ホーム", "介護老人保健施設", "障害者支援施設", "病院", "クリニック", "デイサービス", "短期入所(ショートステイ)", "その他"].includes(form.visit_location) && (
                      <option value={form.visit_location}>{form.visit_location} (登録外)</option>
                    )}
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-[#2C5545]">日常生活の状況</Label>
                <p className="text-[10px] text-[#8A8A86] mb-1">食生活、清潔、排泄、睡眠、生活リズム、部屋の整頓 等</p>
                <Textarea data-testid="daily-living-input" rows={3} value={form.daily_living} onChange={(e) => setForm({ ...form, daily_living: e.target.value })} />
              </div>

              <div>
                <Label className="text-[#2C5545]">精神状態 / 服薬等の状況 / 作業・対人関係について</Label>
                <Textarea data-testid="mental-state-input" rows={3} value={form.mental_state} onChange={(e) => setForm({ ...form, mental_state: e.target.value })} />
              </div>

              <div>
                <Label className="text-[#2C5545]">実施した看護内容</Label>
                <Textarea data-testid="nursing-actions-input" rows={3} value={form.nursing_actions} onChange={(e) => setForm({ ...form, nursing_actions: e.target.value })} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">GAF スコア (1〜100)</Label>
                  <Input
                    data-testid="gaf-score-input"
                    type="number"
                    min={1}
                    max={100}
                    value={form.gaf_score}
                    onChange={(e) => setForm({ ...form, gaf_score: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">次回の訪問予定日</Label>
                  <Input
                    data-testid="next-visit-input"
                    type="date"
                    value={form.next_visit_date}
                    onChange={(e) => setForm({ ...form, next_visit_date: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#2C5545]">バイタル</Label>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-2">
                  <div><Label className="text-xs">体温 ℃</Label><Input value={form.temperature} onChange={(e) => setForm({ ...form, temperature: e.target.value })} /></div>
                  <div><Label className="text-xs">収縮期</Label><Input value={form.bp_systolic} onChange={(e) => setForm({ ...form, bp_systolic: e.target.value })} /></div>
                  <div><Label className="text-xs">拡張期</Label><Input value={form.bp_diastolic} onChange={(e) => setForm({ ...form, bp_diastolic: e.target.value })} /></div>
                  <div><Label className="text-xs">脈拍</Label><Input value={form.pulse} onChange={(e) => setForm({ ...form, pulse: e.target.value })} /></div>
                  <div><Label className="text-xs">SpO2 %</Label><Input value={form.spo2} onChange={(e) => setForm({ ...form, spo2: e.target.value })} /></div>
                  <div><Label className="text-xs">呼吸</Label><Input value={form.respiration} onChange={(e) => setForm({ ...form, respiration: e.target.value })} /></div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label className="text-[#2C5545]">S (主観)</Label><Textarea rows={3} value={form.soap_s} onChange={(e) => setForm({ ...form, soap_s: e.target.value })} /></div>
                <div><Label className="text-[#2C5545]">O (客観)</Label><Textarea rows={3} value={form.soap_o} onChange={(e) => setForm({ ...form, soap_o: e.target.value })} /></div>
                <div><Label className="text-[#2C5545]">A (アセスメント)</Label><Textarea rows={3} value={form.soap_a} onChange={(e) => setForm({ ...form, soap_a: e.target.value })} /></div>
                <div><Label className="text-[#2C5545]">P (計画)</Label><Textarea rows={3} value={form.soap_p} onChange={(e) => setForm({ ...form, soap_p: e.target.value })} /></div>
              </div>

              <div><Label>処置内容</Label><Textarea rows={2} value={form.procedures} onChange={(e) => setForm({ ...form, procedures: e.target.value })} /></div>

              <div className="p-3 rounded-lg border border-[#2C5545]/30 bg-[#F8FAF8]">
                <Label className="text-[#2C5545] font-bold mb-2 block">観察事項(各項目を選択)</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-2" data-testid="observation-details-grid">
                  {OBSERVATION_CHECK_ITEMS.map((item) => {
                    const opts = OBSERVATION_OPTIONS[item] || [];
                    const current = (form.observation_details && form.observation_details[item]) || "";
                    return (
                      <div key={item} className="flex items-center gap-2">
                        <Label className="text-xs text-[#2C5545] shrink-0 w-24 text-right">{item}</Label>
                        <select
                          data-testid={`observation-detail-${item}`}
                          value={current}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              observation_details: { ...(form.observation_details || {}), [item]: e.target.value },
                            })
                          }
                          className="flex h-8 w-full rounded-md border border-input px-2 text-sm bg-white"
                        >
                          <option value="">未選択</option>
                          {opts.map((o) => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                          {current && !opts.includes(current) && (
                            <option value={current}>{current} (登録外)</option>
                          )}
                        </select>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2">
                  <Label className="text-xs text-[#8A8A86]">観察事項 補足メモ(自由記述)</Label>
                  <Textarea
                    rows={2}
                    value={form.observations}
                    onChange={(e) => setForm({ ...form, observations: e.target.value })}
                    placeholder="その他の観察事項・特記事項を記入"
                  />
                </div>
              </div>

              <div className="p-3 rounded-lg border border-[#4A6B8C]/30 bg-[#F4F7FA]">
                <Label className="text-[#4A6B8C] font-bold mb-2 block">入浴・整容(各項目を選択)</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-2" data-testid="bath-grooming-details-grid">
                  {BATH_GROOMING_ITEMS.map((item) => {
                    const current = (form.bath_grooming_details && form.bath_grooming_details[item]) || "";
                    return (
                      <div key={item} className="flex items-center gap-2">
                        <Label className="text-xs text-[#4A6B8C] shrink-0 w-24 text-right">{item}</Label>
                        <select
                          data-testid={`bath-grooming-detail-${item}`}
                          value={current}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              bath_grooming_details: { ...(form.bath_grooming_details || {}), [item]: e.target.value },
                            })
                          }
                          className="flex h-8 w-full rounded-md border border-input px-2 text-sm bg-white"
                        >
                          <option value="">未選択</option>
                          {BATH_GROOMING_OPTIONS.map((o) => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                          {current && !BATH_GROOMING_OPTIONS.includes(current) && (
                            <option value={current}>{current} (登録外)</option>
                          )}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setOpen(false); setEditId(null); setForm(initForm()); }}>キャンセル</Button>
              <Button data-testid="save-record-button" className="bg-[#2C5545] text-white" onClick={save}>{editId ? "更新" : "保存"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-[#E5E5E3] bg-[#FBFBF9] p-4" data-testid="records-filters">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={16} className="text-[#2C5545]" />
          <span className="text-sm font-bold text-[#1C1C1A]">フィルタ</span>
          {filtersActive && (
            <button
              data-testid="clear-filters-button"
              onClick={clearFilters}
              className="ml-1 text-xs flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#A74141]/40 text-[#A74141] hover:bg-[#A74141]/10"
            >
              <X size={11} /> クリア
            </button>
          )}
          <Badge variant="outline" className="ml-auto font-numeric border-[#2C5545]/30 text-[#2C5545]" data-testid="filtered-count">
            該当 {filteredItems.length} / 全 {items.length} 件
          </Badge>
          {isAdmin && (
            <Button
              data-testid="batch-print-button"
              onClick={printBatch}
              disabled={filteredItems.length === 0}
              className="bg-[#2C5545] text-white hover:bg-[#1F3D31] disabled:opacity-40"
              size="sm"
            >
              <Printer size={14} className="mr-1" />
              一括印刷 ({filteredItems.length})
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <div>
            <Label className="text-[10px] text-[#8A8A86]">利用者</Label>
            <select
              data-testid="filter-patient"
              value={patientFilter}
              onChange={(e) => setPatientFilter(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input px-2 text-sm bg-white"
            >
              <option value="">全ての利用者</option>
              {patients.map((p) => <option key={p.id} value={p.id}>{p.name_kanji}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] text-[#8A8A86]">記録日(From)</Label>
            <Input
              data-testid="filter-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-9"
            />
          </div>
          <div>
            <Label className="text-[10px] text-[#8A8A86]">記録日(To)</Label>
            <Input
              data-testid="filter-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-9"
            />
          </div>
          <div>
            <Label className="text-[10px] text-[#8A8A86]">看護師等氏名</Label>
            <select
              data-testid="filter-nurse"
              value={nurseFilter}
              onChange={(e) => setNurseFilter(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input px-2 text-sm bg-white"
            >
              <option value="">全員</option>
              {staff.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
              {/* 過去記録に登録外の名前がある場合も含める */}
              {Array.from(new Set(items.map((r) => r.nurse_name).filter((n) => n && !staff.some((s) => s.name === n))))
                .map((n) => <option key={n} value={n}>{n} (登録外)</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] text-[#8A8A86]">訪問職種</Label>
            <select
              data-testid="filter-profession"
              value={professionFilter}
              onChange={(e) => setProfessionFilter(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input px-2 text-sm bg-white"
            >
              <option value="">全職種</option>
              {["看護師", "准看護師", "保健師", "理学療法士", "作業療法士", "言語聴覚士", "精神保健福祉士", "その他"].map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      <Card className="border-[#E5E5E3] bg-white">
        {filteredItems.length === 0 ? (
          <div className="py-16 text-center text-[#8A8A86]">
            {items.length === 0 ? "記録がありません" : "フィルタ条件に一致する記録がありません"}
          </div>
        ) : (
          <div className="divide-y divide-[#E5E5E3]">
            {filteredItems.map((r) => (
              <div key={r.id} className="p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <Activity size={16} className="text-[#2C5545]" />
                      <span className="font-medium">{patientName(r.patient_id)}</span>
                      <Badge variant="outline" className="font-numeric">{r.record_date}</Badge>
                    </div>
                    {r.summary && <div className="text-sm text-[#4A4A48] mt-1">{r.summary}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`view-record-${r.id}`}
                      onClick={() => setViewRecord(r)}
                      className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-[#2C5545] text-[#2C5545] hover:bg-[#2C5545] hover:text-white transition-colors"
                      title="詳細を表示"
                    >
                      <Eye size={14} /> 詳細
                    </button>
                    <button
                      data-testid={`edit-record-${r.id}`}
                      onClick={() => startEdit(r)}
                      className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-[#8C7A4A] text-[#8C7A4A] hover:bg-[#8C7A4A] hover:text-white transition-colors"
                      title="編集"
                    >
                      <Pencil size={14} /> 編集
                    </button>
                    <button
                      data-testid={`print-record-${r.id}`}
                      onClick={() => printRecord(r)}
                      className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-[#4A6B8C] text-[#4A6B8C] hover:bg-[#4A6B8C] hover:text-white transition-colors"
                      title="印刷"
                    >
                      <Printer size={14} /> 印刷
                    </button>
                    <button
                      data-testid={`delete-record-${r.id}`}
                      onClick={() => del(r.id)}
                      className="text-[#A74141] p-1 rounded hover:bg-[#A74141]/10"
                      title="削除"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs font-numeric text-[#4A4A48] mb-2">
                  {r.temperature && <div>体温 {r.temperature}℃</div>}
                  {r.bp_systolic && <div>BP {r.bp_systolic}/{r.bp_diastolic}</div>}
                  {r.pulse && <div>脈 {r.pulse}</div>}
                  {r.spo2 && <div>SpO2 {r.spo2}%</div>}
                  {r.respiration && <div>呼吸 {r.respiration}</div>}
                </div>
                {(r.soap_s || r.soap_o || r.soap_a || r.soap_p) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    {r.soap_s && <div><span className="text-[#2C5545] font-bold">S:</span> {r.soap_s}</div>}
                    {r.soap_o && <div><span className="text-[#2C5545] font-bold">O:</span> {r.soap_o}</div>}
                    {r.soap_a && <div><span className="text-[#2C5545] font-bold">A:</span> {r.soap_a}</div>}
                    {r.soap_p && <div><span className="text-[#2C5545] font-bold">P:</span> {r.soap_p}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* 看護記録 詳細表示 Dialog (読み取り専用) */}
      <Dialog open={!!viewRecord} onOpenChange={(v) => !v && setViewRecord(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="view-record-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity size={18} className="text-[#2C5545]" />
              看護記録 詳細
              {viewRecord && (
                <Badge variant="outline" className="font-numeric ml-2">{viewRecord.record_date}</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {viewRecord && (
            <div className="space-y-4 py-2 text-sm">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <ReadField label="利用者" value={patientName(viewRecord.patient_id)} />
                <ReadField label="記録日" value={viewRecord.record_date} />
                <ReadField label="次回訪問予定日" value={viewRecord.next_visit_date} />
                <ReadField label="看護師等氏名" value={viewRecord.nurse_name} />
                <ReadField label="訪問職種" value={viewRecord.visit_profession} />
                <ReadField label="訪問先" value={viewRecord.visit_location} />
              </div>

              {viewRecord.daily_living_details && Object.entries(viewRecord.daily_living_details).filter(([, v]) => v && v.trim()).length > 0 && (
                <div>
                  <div className="text-[#2C5545] font-bold text-xs mb-1">日常生活の状況(項目別)</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-1 text-sm">
                    {Object.entries(viewRecord.daily_living_details).filter(([, v]) => v && v.trim()).map(([k, v]) => (
                      <div key={k} className="flex gap-2">
                        <span className="text-[#2C5545] shrink-0 w-24 text-right">{k}:</span>
                        <span className="text-[#1F1F1D] whitespace-pre-wrap">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <ReadBlock label="日常生活の状況 補足" value={viewRecord.daily_living} />
              <ReadBlock label="精神状態 / 服薬等の状況 / 作業・対人関係について" value={viewRecord.mental_state} />
              <ReadBlock label="実施した看護内容" value={viewRecord.nursing_actions} />

              <div>
                <div className="text-[#2C5545] font-medium mb-2">バイタル</div>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2 font-numeric">
                  <ReadField label="体温℃" value={viewRecord.temperature} small />
                  <ReadField label="収縮期" value={viewRecord.bp_systolic} small />
                  <ReadField label="拡張期" value={viewRecord.bp_diastolic} small />
                  <ReadField label="脈拍" value={viewRecord.pulse} small />
                  <ReadField label="SpO2%" value={viewRecord.spo2} small />
                  <ReadField label="呼吸" value={viewRecord.respiration} small />
                </div>
              </div>

              <ReadField label="GAFスコア" value={viewRecord.gaf_score} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ReadBlock label="S (主観)" value={viewRecord.soap_s} accent />
                <ReadBlock label="O (客観)" value={viewRecord.soap_o} accent />
                <ReadBlock label="A (アセスメント)" value={viewRecord.soap_a} accent />
                <ReadBlock label="P (計画)" value={viewRecord.soap_p} accent />
              </div>

              <ReadBlock label="処置内容" value={viewRecord.procedures} />

              {viewRecord.observation_details && Object.entries(viewRecord.observation_details).filter(([, v]) => v && v.trim()).length > 0 && (
                <div>
                  <div className="text-[#2C5545] font-bold text-xs mb-1">観察事項(項目別)</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-1 text-sm">
                    {Object.entries(viewRecord.observation_details).filter(([, v]) => v && v.trim()).map(([k, v]) => (
                      <div key={k} className="flex gap-2">
                        <span className="text-[#2C5545] shrink-0 w-24 text-right">{k}:</span>
                        <span className="text-[#1F1F1D] whitespace-pre-wrap">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <ReadBlock label="観察事項 補足メモ" value={viewRecord.observations} />

              {(() => {
                const details = (viewRecord.bath_grooming_details && typeof viewRecord.bath_grooming_details === "object")
                  ? Object.entries(viewRecord.bath_grooming_details).filter(([, v]) => v && String(v).trim())
                  : [];
                const legacy = Array.isArray(viewRecord.bath_grooming_checks) ? viewRecord.bath_grooming_checks : [];
                if (details.length > 0) {
                  return (
                    <div>
                      <div className="text-[#4A6B8C] font-bold text-xs mb-1">入浴・整容(項目別)</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-1 text-sm">
                        {details.map(([k, v]) => (
                          <div key={k} className="flex gap-2">
                            <span className="text-[#4A6B8C] shrink-0 w-24 text-right">{k}:</span>
                            <span className="text-[#1F1F1D]">{v}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                if (legacy.length > 0) {
                  return (
                    <div>
                      <div className="text-[#4A6B8C] font-bold text-xs mb-1">入浴・整容(実施済)</div>
                      <div className="flex flex-wrap gap-1.5">
                        {legacy.map((c) => (
                          <span key={c} className="px-2 py-0.5 text-xs rounded-full bg-[#4A6B8C] text-white">{c}</span>
                        ))}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
              <ReadBlock label="サマリー" value={viewRecord.summary} />
            </div>
          )}
          <DialogFooter>
            <Button
              data-testid="print-from-view"
              variant="outline"
              onClick={() => printRecord(viewRecord)}
              className="border-[#4A6B8C] text-[#4A6B8C] hover:bg-[#4A6B8C] hover:text-white"
            >
              <Printer size={14} className="mr-1" /> 印刷
            </Button>
            <Button
              data-testid="edit-from-view"
              className="bg-[#8C7A4A] text-white hover:bg-[#7A6A3F]"
              onClick={() => { const r = viewRecord; setViewRecord(null); startEdit(r); }}
            >
              <Pencil size={14} className="mr-1" /> この記録を編集
            </Button>
            <Button
              data-testid="close-view-record"
              variant="outline"
              onClick={() => setViewRecord(null)}
              className="border-[#2C5545] text-[#2C5545]"
            >
              閉じる
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function roleLabel(r) {
  return { tenant_admin: "管理者", nurse: "看護師", office: "事務", super_admin: "運営" }[r] || r;
}

function ReadField({ label, value, small }) {
  return (
    <div>
      <div className={`text-[#8A8A86] ${small ? "text-[10px]" : "text-xs"} mb-0.5`}>{label}</div>
      <div className={`${small ? "text-xs" : "text-sm"} text-[#1F1F1D]`}>
        {value || value === 0 ? value : <span className="text-[#C5C5C2]">—</span>}
      </div>
    </div>
  );
}

function ReadBlock({ label, value, accent }) {
  return (
    <div>
      <div className={`text-xs mb-1 ${accent ? "text-[#2C5545] font-bold" : "text-[#8A8A86]"}`}>{label}</div>
      <div className="text-sm text-[#1F1F1D] whitespace-pre-wrap rounded-md bg-[#F8F8F6] border border-[#E5E5E3] px-3 py-2 min-h-[2.25rem]">
        {value || <span className="text-[#C5C5C2]">記載なし</span>}
      </div>
    </div>
  );
}
